import type { EditorView } from '@codemirror/view'

/**
 * Shared image attachment utility.
 *
 * Follows the companion-folder convention:
 *   readme.md → readme.md.attachments/paste-m1abc.png
 *
 * Used by both paste and drop handlers to eliminate duplication.
 */

interface AttachImageOptions {
    /** The File/Blob to attach */
    blob: Blob
    /** Absolute workspace path of the current file (e.g. /notes/readme.md) */
    filePath: string
    /** Write binary bytes to the workspace FS */
    writeBytes: (path: string, bytes: Uint8Array) => void
    /** Optional CodeMirror editor view for cursor-position insertion */
    editorView?: EditorView | null
    /** Fallback: append to content if no editorView */
    updateContent?: (content: string) => void
    /** Current file content (for fallback append) */
    content?: string | null
    /** Prefix for the generated filename (e.g. 'paste' or 'drop') */
    prefix?: string
}

interface AttachImageResult {
    /** The absolute workspace path where the image was stored */
    absPath: string
    /** The relative markdown reference path */
    relPath: string
    /** The markdown image reference string */
    mdRef: string
}

export async function attachImage({
    blob,
    filePath,
    writeBytes,
    editorView,
    updateContent,
    content,
    prefix = 'paste',
}: AttachImageOptions): Promise<AttachImageResult> {
    const ext = extensionFromMimeType(blob.type)
    const shortId = Date.now().toString(36) + Math.floor(Math.random() * 0x10000).toString(36).padStart(3, '0')
    const attachDir = `${filePath}.attachments`
    const fileName = `${prefix}-${shortId}.${ext}`
    const absPath = `${attachDir}/${fileName}`

    // Relative path from the file's directory
    const baseName = filePath.split('/').pop() || ''
    const relPath = `${baseName}.attachments/${fileName}`
    const mdRef = `![image](${relPath})`

    // Write binary — no data-URL workarounds
    const buffer = await blob.arrayBuffer()
    writeBytes(absPath, new Uint8Array(buffer))

    // Insert markdown reference at cursor or append
    if (editorView) {
        const pos = editorView.state.selection.main.head
        editorView.dispatch({
            changes: { from: pos, insert: mdRef },
            selection: { anchor: pos + mdRef.length },
        })
        editorView.focus()
    } else if (updateContent) {
        updateContent((content || '') + '\n' + mdRef + '\n')
    }

    return { absPath, relPath, mdRef }
}

function extensionFromMimeType(mimeType: string): string {
    const rawSubtype = mimeType.split('/')[1]?.toLowerCase() ?? ''
    const subtype = rawSubtype.split(';')[0]

    if (!subtype) return 'png'
    if (subtype === 'jpeg') return 'jpg'

    // Common structured MIME subtypes (e.g. image/svg+xml) should map to a plain file extension.
    if (subtype.includes('+')) {
        return subtype.split('+')[0] || 'png'
    }

    return subtype
}
